// Status de cotação — modelo de oportunidade (estilo CRM).
// Rascunho    -> em construção pelo agente (único estado editável)
// Enviada     -> PDF/link já compartilhado com o cliente (estática)
// Convertida  -> virou reserva/venda (estática)
// Perdida     -> oportunidade perdida (cliente recusou/fechou em outro lugar)
// Sem retorno -> oportunidade perdida por falta de resposta do cliente
export const QUOTE_STATUS = {
	RASCUNHO: 'rascunho',
	ENVIADA: 'enviada',
	CONVERTIDA: 'convertida',
	PERDIDA: 'perdida',
	SEM_RETORNO: 'sem_retorno'
};

// Opções para dropdowns/filtros + severity do Tag (PrimeReact) para o badge.
export const QUOTE_STATUS_OPTIONS = [
	{ value: QUOTE_STATUS.RASCUNHO, label: 'Rascunho', severity: 'warning' },
	{ value: QUOTE_STATUS.ENVIADA, label: 'Enviada', severity: 'info' },
	{ value: QUOTE_STATUS.CONVERTIDA, label: 'Convertida', severity: 'success' },
	// Mesmo rótulo do funil Fase 2 — o slug é compartilhado e o usuário vê as duas
	// listas na mesma tela; divergir aqui criaria dois nomes para o mesmo estado.
	{ value: QUOTE_STATUS.PERDIDA, label: 'Venda perdida', severity: 'danger' },
	{ value: QUOTE_STATUS.SEM_RETORNO, label: 'Sem retorno', severity: 'secondary' }
];

export const quoteStatusLabel = (value) =>
	(QUOTE_STATUS_OPTIONS.find((o) => o.value === value) || {}).label || value;

export const quoteStatusSeverity = (value) =>
	(QUOTE_STATUS_OPTIONS.find((o) => o.value === value) || {}).severity || 'secondary';

// Regras de imutabilidade (CRM): só RASCUNHO é editável; Enviada/Convertida são
// estáticas (não excluir/alterar). Para alterar, voltar para Rascunho.
export const quotePodeEditar = (status) => status === QUOTE_STATUS.RASCUNHO;

// EXCLUIR: só no RASCUNHO (owner 2026-08-07). Antes a regra era "tudo menos enviada e
// convertida", o que deixava apagar uma cotação PERDIDA — e perdida é o registro de que a
// oportunidade existiu e como terminou. Apagar isso fura o funil e vai furar o CRM que
// será integrado. A partir de "enviada"/"aguardando seleção" o cliente já viu a proposta:
// o desfecho é histórico, não lixo.
//
// O BE já aplicava esta regra (`QuoteService.remove` → 409 QUOTE_LOCKED, "Só cotações em
// rascunho podem ser excluídas"); o botão que a UI mostrava fora do rascunho era uma ação
// que a API recusava. Vale para os dois fluxos — legado e Fase 2 compartilham o slug.
export const quotePodeExcluir = (status) => status === QUOTE_STATUS.RASCUNHO;

// Indica se o regime do quarto inclui café da manhã (café, meia pensão,
// pensão completa, all inclusive / tudo incluído).
export const temCafeDaManha = (regime) =>
	/caf[ée]|breakfast|meia\s*pens|pens[ãa]o|all[\s-]*inclus|tudo\s*inclu/i.test(String(regime || ''));

// Café da manhã do item: usa a flag explícita `quarto.cafe` (vinda do value-add
// "free_breakfast") quando existir; senão, deduz do texto do regime.
export const quartoTemCafe = (quarto) => {
	if (!quarto) return false;
	if (quarto.cafe === true) return true;
	if (quarto.cafe === false) return false;
	return temCafeDaManha(quarto.regime);
};

// Conflito de período: dois intervalos que SE SOBREPÕEM mas NÃO são idênticos.
// (Mesmo período exato = outra opção para a mesma estadia → permitido.
//  Períodos diferentes que se sobrepõem = conflito, ex.: 23–25 e 24–25.)
export const periodosConflitam = (a, b) => {
	if (!a || !b || !a.checkIn || !a.checkOut || !b.checkIn || !b.checkOut) return false;
	const mesmo = a.checkIn === b.checkIn && a.checkOut === b.checkOut;
	if (mesmo) return false;
	// overlap: aIn < bOut && bIn < aOut (datas YYYY-MM-DD comparáveis lexicograficamente)
	return a.checkIn < b.checkOut && b.checkIn < a.checkOut;
};

// Item DUPLICADO exato: mesmo hotel + quarto (nome+tarifa) + mesmo período.
// (Diferente de periodosConflitam, que trata sobreposição parcial.)
export const isMesmoItem = (a, b) =>
	!!a && !!b &&
	a.hotel?.id === b.hotel?.id &&
	(a.quarto?.nome || '') === (b.quarto?.nome || '') &&
	(a.quarto?.tarifa || '') === (b.quarto?.tarifa || '') &&
	a.periodo?.checkIn === b.periodo?.checkIn &&
	a.periodo?.checkOut === b.periodo?.checkOut;

// Direitos (espelham o padrão userRights.includes(...) do projeto).
// listQuotes        -> acessa a área de cotações / vê as da agência
// viewOwnQuotesOnly -> restringe a ver apenas as cotações criadas pelo próprio usuário
export const QUOTE_RIGHTS = {
	LIST: 'listQuotes',
	OWN_ONLY: 'viewOwnQuotesOnly'
};

// ===========================================================================
// COTAÇÃO FASE 2 — funil semi-automático com autosserviço do cliente
// Atrás de FEATURE_FLAGS.COTACAO_FASE2. Aditivo — não altera o fluxo legado
// acima (rascunho/enviada/convertida/perdida/sem_retorno segue em produção).
// Spec: .context/specs/cotacao-fase2-funil-cliente.md
// ===========================================================================

// Máquina de estados (fonte da verdade na cotação).
export const QUOTE_STATUS_F2 = {
	RASCUNHO:            'rascunho',            // 1 - agência monta (único editável)
	AGUARDANDO_SELECAO:  'aguardando_selecao',  // 2 - enviada; cliente seleciona+aprova
	APROVADA:            'aprovada',            // 3 - aprovada; aguardando reserva
	RESERVADA:           'reservada',           // 4 - reservas feitas; aguardando pagamento
	GANHA:               'ganha',               // 5 - todas as reservas pagas (venda)
	PARCIALMENTE_GANHA:  'parcialmente_ganha',  // 5b - parte paga, parte perdida
	PERDIDA:             'perdida',             // 6 - terminal (motivo + estágio)
};

// Ordem no funil + label + severity do Tag (PrimeReact).
export const QUOTE_STATUS_F2_OPTIONS = [
	{ value: QUOTE_STATUS_F2.RASCUNHO,           label: 'Rascunho',                 severity: 'warning',   ordem: 1 },
	{ value: QUOTE_STATUS_F2.AGUARDANDO_SELECAO, label: 'Aguardando seleção',       severity: 'info',      ordem: 2 },
	{ value: QUOTE_STATUS_F2.APROVADA,           label: 'Aprovada',                 severity: 'info',      ordem: 3 },
	{ value: QUOTE_STATUS_F2.RESERVADA,          label: 'Reserva realizada',        severity: 'info',      ordem: 4 },
	// Trio do desfecho lido como VENDA (owner 2026-08-05): ganha / parcialmente ganha
	// / perdida falam do resultado comercial, não do documento.
	{ value: QUOTE_STATUS_F2.GANHA,              label: 'Venda ganha',              severity: 'success',   ordem: 5 },
	{ value: QUOTE_STATUS_F2.PARCIALMENTE_GANHA, label: 'Venda parcialmente ganha', severity: 'success',   ordem: 5 },
	{ value: QUOTE_STATUS_F2.PERDIDA,            label: 'Venda perdida',            severity: 'danger',    ordem: 6 },
];

export const quoteF2StatusLabel = (value) =>
	(QUOTE_STATUS_F2_OPTIONS.find((o) => o.value === value) || {}).label || value;
export const quoteF2StatusSeverity = (value) =>
	(QUOTE_STATUS_F2_OPTIONS.find((o) => o.value === value) || {}).severity || 'secondary';

// Motivo da perda (junto com o estágio, cobre 4.1/4.2/7.1/7.2 do funil).
export const QUOTE_LOSS_MOTIVO = {
	SEM_RETORNO:              'sem_retorno',
	RECUSADA_PELO_CLIENTE:    'recusada_pelo_cliente',
	DESISTENCIA:              'desistencia',
	INDISPONIBILIDADE:        'indisponibilidade',
	CANCELADA_POR_PRAZO:      'cancelada_por_prazo',
	CANCELADA_CLIENTE_AGENCIA:'cancelada_cliente_ou_agencia',
};

// Motivos oferecidos por estágio (para o modal "marcar perdida").
export const QUOTE_LOSS_MOTIVO_OPTIONS = {
	[QUOTE_STATUS_F2.AGUARDANDO_SELECAO]: [
		{ value: QUOTE_LOSS_MOTIVO.RECUSADA_PELO_CLIENTE, label: 'Recusada pelo cliente (com feedback)' },
		{ value: QUOTE_LOSS_MOTIVO.SEM_RETORNO,           label: 'Sem retorno do cliente' },
	],
	[QUOTE_STATUS_F2.APROVADA]: [
		{ value: QUOTE_LOSS_MOTIVO.DESISTENCIA,       label: 'Desistência do cliente' },
		{ value: QUOTE_LOSS_MOTIVO.INDISPONIBILIDADE, label: 'Indisponibilidade' },
	],
	[QUOTE_STATUS_F2.RESERVADA]: [
		{ value: QUOTE_LOSS_MOTIVO.CANCELADA_POR_PRAZO,       label: 'Cancelada por prazo de pagamento' },
		{ value: QUOTE_LOSS_MOTIVO.CANCELADA_CLIENTE_AGENCIA, label: 'Cancelada pelo cliente ou agência' },
	],
};
export const quoteLossMotivoLabel = (value) => {
	for (const arr of Object.values(QUOTE_LOSS_MOTIVO_OPTIONS)) {
		const found = arr.find((o) => o.value === value);
		if (found) return found.label;
	}
	return value;
};

// Editabilidade: só RASCUNHO deixa mexer nos itens/opções.
export const quoteF2PodeEditarItens = (status) => status === QUOTE_STATUS_F2.RASCUNHO;
export const quoteF2Travada = (status) => status !== QUOTE_STATUS_F2.RASCUNHO;
export const quoteF2Terminal = (status) =>
	status === QUOTE_STATUS_F2.GANHA ||
	status === QUOTE_STATUS_F2.PARCIALMENTE_GANHA ||
	status === QUOTE_STATUS_F2.PERDIDA;

// Ações disponíveis por status (dirige a barra de ações da agência).
export const quoteF2Acoes = (status) => ({
	editar:           status === QUOTE_STATUS_F2.RASCUNHO,
	// PDF só faz sentido enquanto a cotação é uma PROPOSTA (rascunho/aguardando).
	// De aprovada em diante ela virou reserva — o documento útil passa a ser o
	// voucher, não a cotação (owner 2026-08-04).
	baixarPdf:        status === QUOTE_STATUS_F2.RASCUNHO || status === QUOTE_STATUS_F2.AGUARDANDO_SELECAO,
	enviarSelecao:    status === QUOTE_STATUS_F2.RASCUNHO,
	desfazerEnvio:    status === QUOTE_STATUS_F2.AGUARDANDO_SELECAO,
	reenviarEmail:    status === QUOTE_STATUS_F2.AGUARDANDO_SELECAO,
	operarComoCliente:status === QUOTE_STATUS_F2.AGUARDANDO_SELECAO,
	reabrirAjuste:    status === QUOTE_STATUS_F2.APROVADA,
	prosseguirReserva:status === QUOTE_STATUS_F2.APROVADA,
	// Perda manual só ANTES de reservar. Em RESERVADA, a perda deriva
	// automaticamente do cancelamento/pagamento das reservas vinculadas.
	marcarPerdida:    status === QUOTE_STATUS_F2.AGUARDANDO_SELECAO ||
	                  status === QUOTE_STATUS_F2.APROVADA,
	reabrirPerdida:   status === QUOTE_STATUS_F2.PERDIDA,
});

// ---- Trechos (ponto de decisão do cliente) --------------------------------
// Um trecho agrupa opções MUTUAMENTE EXCLUSIVAS (o cliente escolhe no máximo uma).
// Trechos em datas complementares formam o roteiro (aditivos).
// Cada item traz `trechoId`; a cotação traz `trechos:[{id,label,obrigatorio,periodoLabel}]`.
// Fallback (dados sem trecho): agrupa por período exato.
export const trechoKeyDoItem = (item) =>
	item?.trechoId || `${item?.periodo?.checkIn || ''}_${item?.periodo?.checkOut || ''}`;

export const agruparTrechos = (quote) => {
	const itens = quote?.itens || [];
	const defs = quote?.trechos || [];
	const byKey = new Map();
	itens.forEach((it) => {
		const k = trechoKeyDoItem(it);
		if (!byKey.has(k)) byKey.set(k, []);
		byKey.get(k).push(it);
	});
	// Preserva a ordem das defs quando existirem; senão ordena por checkIn.
	const keysOrdenadas = defs.length
		? defs.map((d) => d.id)
		: Array.from(byKey.keys()).sort();
	return keysOrdenadas
		.filter((k) => byKey.has(k))
		.map((k) => {
			const def = defs.find((d) => d.id === k) || {};
			const opcoes = byKey.get(k) || [];
			const p = opcoes[0]?.periodo;
			return {
				id: k,
				label: def.label || opcoes[0]?.hotel?.cidade || 'Trecho',
				obrigatorio: def.obrigatorio !== false, // default: obrigatório
				periodo: p,
				periodoLabel: def.periodoLabel || (p ? `${p.checkIn} → ${p.checkOut}` : ''),
				opcoes,
			};
		});
};

// Vão máximo (em noites) que ainda é tratado como LACUNA de roteiro e vira aviso na tela.
// Acima disso a interpretação natural é "duas viagens na mesma cotação" — avisar seria ruído.
export const LACUNA_ROTEIRO_MAX_NOITES = 3;

// Valida a seleção do cliente. `selecao` = { [trechoId]: itemId | null }.
// Retorna { ok, obrigatoriosPendentes:[], algumSelecionado, parcial, naoSelecionados, buracos }.
// `buracos` = [{ de, ate, noites }] — TODOS os vãos; filtrar por `LACUNA_ROTEIRO_MAX_NOITES`
// é decisão de apresentação, não de validação (nada aqui bloqueia a aprovação).
//
// 2026-08-03 — TODO bloco é OPCIONAL. Não existe mais trecho obrigatório: a única
// regra é "pelo menos uma opção selecionada", o que habilita a APROVAÇÃO PARCIAL
// (o cliente leva só os blocos que quiser; o resto é descartado para reserva).
// `obrigatoriosPendentes` continua no retorno, sempre vazio, para não quebrar
// quem ainda lê o campo.
export const validarSelecao = (trechos, selecao = {}) => {
	const obrigatoriosPendentes = [];
	const selecionados = trechos.filter((t) => !!selecao[t.id]);
	const algumSelecionado = selecionados.length > 0;
	// Buracos: dias descobertos entre trechos selecionados consecutivos (só aviso).
	const periodos = selecionados
		.map((t) => {
			const it = t.opcoes.find((o) => o.id === selecao[t.id]);
			return it?.periodo;
		})
		.filter((p) => p && p.checkIn && p.checkOut)
		.sort((a, b) => (a.checkIn < b.checkIn ? -1 : 1));
	const buracos = [];
	for (let i = 1; i < periodos.length; i++) {
		if (periodos[i].checkIn > periodos[i - 1].checkOut) {
			const de = periodos[i - 1].checkOut, ate = periodos[i].checkIn;
			// `noites` qualifica o vão: 2 noites entre trechos é lacuna de roteiro; 3 semanas
			// são duas viagens distintas na mesma cotação. Sem isso o aviso disparava nos dois
			// casos e o cliente lia um alarme onde não havia nada errado (owner 2026-08-07).
			const noites = Math.max(0, Math.round((new Date(ate) - new Date(de)) / 86400000));
			buracos.push({ de, ate, noites });
		}
	}
	// Parcial = aprovou deixando bloco(s) de fora. Alimenta o modal de confirmação
	// que avisa que o não selecionado é descartado para reserva.
	const naoSelecionados = trechos.filter((t) => !selecao[t.id]).map((t) => t.label || t.periodoLabel || t.id);
	return {
		ok: algumSelecionado,
		obrigatoriosPendentes,
		algumSelecionado,
		parcial: algumSelecionado && naoSelecionados.length > 0,
		naoSelecionados,
		buracos,
	};
};

// Desfecho da reserva POR BLOCO (owner 2026-08-06): { [trechoId]: {status, codigoReserva} }.
// Com mais de uma reserva na mesma cotação, o status do topo ("Venda parcialmente ganha")
// não diz QUAL trecho foi ganho e qual foi perdido — isto casa cada bloco com a sua reserva.
// Casa por `trechoId` (o que o BE devolve) e, na falta dele, pelo item → trecho.
// Compartilhado entre o painel da agência e a página pública do cliente.
export const desfechoPorTrechoDe = (quote) => {
	const reservas = quote?.reservas || [];
	if (!reservas.length) return null;
	const trechoDoItem = new Map((quote?.itens || []).map((i) => [i.id, i.trechoId ?? null]));
	const out = {};
	reservas.forEach((r) => {
		const trechoId = r.trechoId ?? r.QuoteTrechoId ?? trechoDoItem.get(r.itemId ?? r.QuoteItemId) ?? null;
		if (trechoId === null) return; // a cadeia `?? null` acima já normaliza undefined
		out[trechoId] = { status: r.status, codigoReserva: r.codigoReserva || null };
	});
	return Object.keys(out).length ? out : null;
};

// Deriva o status pós-reserva a partir das reservas vinculadas (auto).
// reservas: [{ status: 'pendente'|'paga'|'cancelada' }]. Retorna um QUOTE_STATUS_F2.
export const derivarStatusPorReservas = (reservas = []) => {
	if (!reservas.length) return QUOTE_STATUS_F2.RESERVADA;
	const pagas = reservas.filter((r) => r.status === 'paga').length;
	const canceladas = reservas.filter((r) => r.status === 'cancelada').length;
	const pendentes = reservas.length - pagas - canceladas;
	if (pendentes > 0) return QUOTE_STATUS_F2.RESERVADA;
	if (pagas === reservas.length) return QUOTE_STATUS_F2.GANHA;
	if (pagas === 0) return QUOTE_STATUS_F2.PERDIDA; // todas canceladas
	return QUOTE_STATUS_F2.PARCIALMENTE_GANHA;
};

// Opções de status UNIFICADAS (legado + Fase 2) para o filtro da lista quando a
// flag Fase 2 está ligada. Deduplica os slugs compartilhados (rascunho, perdida).
export const QUOTE_STATUS_ALL_OPTIONS = (() => {
	const byValue = new Map();
	[...QUOTE_STATUS_OPTIONS, ...QUOTE_STATUS_F2_OPTIONS].forEach((o) => {
		if (!byValue.has(o.value)) byValue.set(o.value, o);
	});
	return Array.from(byValue.values());
})();

// Rótulo/severidade de QUALQUER status (F2 tem prioridade; senão legado). Como os
// slugs compartilhados (rascunho/perdida) têm rótulo/severidade iguais nos dois,
// é seguro usar sempre — para status legado devolve o rótulo legado.
export const quoteAnyStatusLabel = (value) =>
	(QUOTE_STATUS_F2_OPTIONS.find((o) => o.value === value)
		|| QUOTE_STATUS_OPTIONS.find((o) => o.value === value) || {}).label || value;
export const quoteAnyStatusSeverity = (value) =>
	(QUOTE_STATUS_F2_OPTIONS.find((o) => o.value === value)
		|| QUOTE_STATUS_OPTIONS.find((o) => o.value === value) || {}).severity || 'secondary';

// Detecta se a cotação pertence ao fluxo Fase 2 (tem trechos ou status novo).
// Usado para o QuoteDetailModal decidir entre o painel Fase 2 e o CRM legado.
export const isCotacaoF2 = (quote) =>
	!!quote && (
		(Array.isArray(quote.trechos) && quote.trechos.length > 0) ||
		[
			QUOTE_STATUS_F2.AGUARDANDO_SELECAO,
			QUOTE_STATUS_F2.APROVADA,
			QUOTE_STATUS_F2.RESERVADA,
			QUOTE_STATUS_F2.GANHA,
			QUOTE_STATUS_F2.PARCIALMENTE_GANHA,
		].includes(quote.status)
	);
